import './styles/index.css'

import type { Content, Editor } from '@tiptap/react'
import { EditorContent } from '@tiptap/react'
import React, { useRef } from 'react'

import { cn } from '../../../lib/utils'
import { Separator } from '../../ui/separator'
import { ImageBubbleMenu } from './components/bubble-menu/image-bubble-menu'
import { LinkBubbleMenu } from './components/bubble-menu/link-bubble-menu'
import { SectionFive } from './components/section/five'
import { SectionFour } from './components/section/four'
import { SectionOne } from './components/section/one'
import { SectionThree } from './components/section/three'
import { SectionTwo } from './components/section/two'
import type { UseMinimalTiptapEditorProps } from './hooks/use-minimal-tiptap'
import { useMinimalTiptapEditor } from './hooks/use-minimal-tiptap'

export interface MinimalTiptapProps extends Omit<UseMinimalTiptapEditorProps, 'onUpdate'> {
    value?: Content
    onChange?: (value: Content) => void
    className?: string
    editorContentClassName?: string
}

const Toolbar = React.forwardRef<HTMLDivElement, { editor: Editor }>(({ editor }, ref) => (
    <div ref={ref} className="shrink-0 overflow-x-auto border-b border-border p-2">
        <div className="flex w-max items-center gap-px">
            <SectionOne editor={editor} activeLevels={[1, 2, 3, 4, 5, 6]} />

            <Separator orientation="vertical" className="mx-2 h-7" />

            <SectionTwo
                editor={editor}
                activeActions={['bold', 'italic', 'strikethrough', 'code', 'clearFormatting']}
                mainActionCount={2}
            />

            <Separator orientation="vertical" className="mx-2 h-7" />

            <SectionThree editor={editor} />

            <Separator orientation="vertical" className="mx-2 h-7" />

            <SectionFour
                editor={editor}
                activeActions={['orderedList', 'bulletList']}
                mainActionCount={0}
            />

            <Separator orientation="vertical" className="mx-2 h-7" />

            <SectionFive
                editor={editor}
                activeActions={['codeBlock', 'blockquote', 'horizontalRule']}
                mainActionCount={0}
            />
        </div>
    </div>
))

export const MinimalTiptapEditor = React.forwardRef<HTMLDivElement, MinimalTiptapProps>(
    ({ value, onChange, className, editorContentClassName, ...props }, ref) => {
        const editor = useMinimalTiptapEditor({
            value,
            onUpdate: onChange,
            ...props,
        })

        const toolbarRef = useRef<HTMLDivElement>(null)

        if (!editor) {
            return null
        }

        return (
            <div
                ref={ref}
                onClick={(e) => {
                    const target = e.target as Node

                    if (!e.currentTarget.contains(target)) {
                        return
                    }

                    if (toolbarRef.current?.contains(target)) {
                        return
                    }

                    if (!editor.isFocused) {
                        editor.commands.focus('end')
                    }
                }}
                className={cn(
                    'flex max-h-[60vh] min-h-72 w-full flex-col rounded-md border border-input shadow-xs focus-within:border-primary hover:cursor-text',
                    className,
                )}
            >
                <Toolbar editor={editor} ref={toolbarRef} />
                <EditorContent
                    editor={editor}
                    className={cn(
                        'minimal-tiptap-editor min-h-0 flex-1 overflow-y-auto',
                        editorContentClassName,
                    )}
                />
                <LinkBubbleMenu editor={editor} />
                <ImageBubbleMenu editor={editor} />
            </div>
        )
    },
)
